import { useEffect, type ReactNode } from 'react';
import { useAuth } from '@next-social-platform/chatbot_sso/react';
import { setAuthedFetch } from '../services/authBridge';

// Sits directly under <AuthProvider>. Registers the SDK's authenticated fetch
// with the hubClient bridge and gates the app on auth state:
//   not primed   → spinner (init() still resolving)
//   error        → message + retry sign-in
//   unauthorized → AuthProvider's login-required already redirected to the IdP;
//                  show a fallback sign-in button in case the redirect was
//                  suppressed (e.g. during an OIDC callback round-trip)
//   authorized   → render the app
function Centered({ children }: { children: ReactNode }) {
  return <div className="flex h-full w-full items-center justify-center bg-gray-50">{children}</div>;
}

export function AppGate({ children }: { children: ReactNode }) {
  const { auth, primed, authenticated, error, login } = useAuth();

  useEffect(() => {
    if (auth) {
      setAuthedFetch((url, init) => auth.apiFetch(url, init));
    }
    return () => setAuthedFetch(null);
  }, [auth]);

  if (!primed) {
    return (
      <Centered>
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="text-gray-500">Authenticating…</p>
        </div>
      </Centered>
    );
  }

  if (error && !authenticated) {
    return (
      <Centered>
        <div className="max-w-sm text-center">
          <p className="mb-3 text-sm text-red-600">{error.message}</p>
          <button onClick={login} className="rounded bg-blue-600 px-4 py-2 text-sm text-white">
            Sign in
          </button>
        </div>
      </Centered>
    );
  }

  if (!authenticated) {
    return (
      <Centered>
        <div className="text-center">
          <p className="mb-3 text-gray-500">Redirecting to sign in…</p>
          <button onClick={login} className="rounded bg-blue-600 px-4 py-2 text-sm text-white">
            Sign in
          </button>
        </div>
      </Centered>
    );
  }

  return <>{children}</>;
}
